import { useCallback } from "react";

import { format, parseISO } from "date-fns";

import { DateFormat } from "@/constants/DateFormat";
import smtApi, { useLazyGetSMTDailyLogsByRangeQuery } from "@/store/api/smt";
import { useAppDispatch } from "@/store/hooks";
import store from "@/store/index";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { findAndUpdateDailyLogInPage } from "@/utils/smt.util";

/**
 * Checks if a specific logDate exists in the getSMTDailyLogs cache.
 */
const checkIfLogDateExistsInCache = ({
  logDate,
  patientId,
}: {
  logDate: string;
  patientId: string;
}): boolean => {
  const state = store.getState();
  const cachedData = smtApi.endpoints.getSMTDailyLogs.select({
    patientId,
  })(state);

  if (!cachedData.data) {
    return false;
  }

  return cachedData.data.pages.some((page) =>
    page.some((log) => log.logDate === logDate),
  );
};

const useSMTDailyLogsCache = () => {
  const dispatch = useAppDispatch();
  const [getSMTDailyLogsByRange] = useLazyGetSMTDailyLogsByRangeQuery();

  /**
   * Manually updates the RTK Query cache for getSMTDailyLogs with fresh data.
   * We use manual cache updates (dispatch with updateQueryData) instead of refetching because:
   * 1. We already have fresh data from getSMTDailyLogsByRange - no need for another API call
   * 2. Manual updates allow us to surgically update the infinite query cache without invalidating/refetching all pages
   */
  const updateCacheWithFreshLog = useCallback(
    ({
      freshLogEntry,
      logDate,
      patientId,
    }: {
      freshLogEntry: SMTDailyLog;
      logDate: string;
      patientId: string;
    }) => {
      dispatch(
        smtApi.util.updateQueryData(
          "getSMTDailyLogs",
          { patientId },
          (cacheData) => ({
            ...cacheData,
            pages: cacheData.pages.map((page) =>
              findAndUpdateDailyLogInPage({ freshLogEntry, logDate, page }),
            ),
          }),
        ),
      );
    },
    [dispatch],
  );

  /**
   * Fetches fresh log data for a specific date and updates the cache.
   */
  const fetchAndUpdateLog = useCallback(
    async ({ logDate, patientId }: { logDate: string; patientId: string }) => {
      const { data: freshLogs } = await getSMTDailyLogsByRange(
        {
          endDate: logDate,
          patientId,
          startDate: logDate,
        },
        // preferCacheValue: false = always fetch fresh logs
        false,
      );

      if (!isNotNilNotEmpty(freshLogs)) return;

      updateCacheWithFreshLog({
        freshLogEntry: freshLogs[0],
        logDate,
        patientId,
      });
    },
    [getSMTDailyLogsByRange, updateCacheWithFreshLog],
  );

  const updateApiSMTDailyLogsCache = useCallback(
    ({ occurredAt, patientId }: { occurredAt: string; patientId: string }) => {
      const logDate = format(parseISO(occurredAt), DateFormat.yearMonthDay);

      // Check if logDate exists in cache and update if necessary
      if (!checkIfLogDateExistsInCache({ logDate, patientId })) return;

      // fire-and-forget to not block the UI from proceeding, as this is only a cache update
      void fetchAndUpdateLog({ logDate, patientId });
    },
    [fetchAndUpdateLog],
  );

  return {
    updateApiSMTDailyLogsCache,
  };
};

export default useSMTDailyLogsCache;
